// Layer 3 (Transformation): recovery trend extrapolation and reagent guardrail
// (cro-brief R2, #322). No curve math — slope from trailing series only.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { ControlChartViewModel } from '@/versions/v1/shared/SpcEngine';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { shiftOf, diffMinutes, parseTime } from '@/versions/v1/shared/clock.derive';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import { REAGENT_READING_BY_ROW, reagentStandingFindings } from './RoleLanding.transform';

const RECOVERY_SERIES: readonly { seriesId: string; readingId: string; metal: string }[] = [
  { seriesId: 'rs-zn-rec', readingId: 'rd-kpi-zn-rec', metal: 'Zn' },
  { seriesId: 'rs-pb-rec', readingId: 'rd-kpi-pb-rec', metal: 'Pb' },
  { seriesId: 'rs-ag-rec', readingId: 'rd-kpi-ag-rec', metal: 'Ag' },
];

const TRAILING_POINTS = 7;

/** D-aero: Aero 5100 is filed under sp-pb but pairs with Zn recovery on the glass. */
const REAGENT_RECOVERY_METAL: Record<string, string> = {
  'rd-reag-aero': 'Zn',
};

function formatNumber(n: number, decimals = 1): string {
  return n.toLocaleString('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
}

function leastSquaresSlopePerHour(
  points: readonly { at: SeriesTime; value: number }[],
): number {
  if (points.length < 2) return 0;
  const t0 = parseTime(points[0]!.at).getTime();
  const xs = points.map((p) => (parseTime(p.at).getTime() - t0) / 3_600_000);
  const ys = points.map((p) => p.value);
  const n = xs.length;
  const sumX = xs.reduce((a, b) => a + b, 0);
  const sumY = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((acc, x, i) => acc + x * ys[i]!, 0);
  const sumXX = xs.reduce((acc, x) => acc + x * x, 0);
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return 0;
  return (n * sumXY - sumX * sumY) / denom;
}

function trailingNumericPoints(
  seriesId: string,
  state: SessionState,
  count: number,
): { at: SeriesTime; value: number }[] {
  const view = seriesAt(seriesId, state);
  return view.points
    .filter((p): p is { at: SeriesTime; value: number } => p.value !== null)
    .slice(-count);
}

function designForReading(readingId: string): number {
  const reading = readingByIdLookup(readingId);
  return reading?.design ?? reading?.target ?? 0;
}

function consecutivePointsBelowDesign(
  seriesId: string,
  design: number,
  state: SessionState,
): number {
  const view = seriesAt(seriesId, state);
  const points = view.points.filter(
    (p): p is { at: SeriesTime; value: number } => p.value !== null,
  );
  let count = 0;
  for (let i = points.length - 1; i >= 0; i--) {
    if (points[i]!.value < design) count += 1;
    else break;
  }
  return count;
}

function shiftsToBreach(
  current: number,
  design: number,
  slopePerHour: number,
): number | 'holding' {
  if (slopePerHour >= 0) return 'holding';
  if (current < design) return 'holding';
  const gap = current - design;
  const hoursToBreach = gap / Math.abs(slopePerHour);
  return Math.max(1, Math.ceil(hoursToBreach / 12));
}

function recoveryMetalForReagent(readingId: string): string {
  if (REAGENT_RECOVERY_METAL[readingId]) return REAGENT_RECOVERY_METAL[readingId]!;
  const tagRow = TAG_MAPPING.find((row) => row.readingId === readingId);
  if (tagRow?.subprocessId === 'sp-zn') return 'Zn';
  return 'Pb';
}

export type MetalForecastRead = {
  metal: string;
  current: number;
  projected: number;
  design: number;
  atRisk: boolean;
  sentence: string;
  points: readonly { at: SeriesTime; value: number }[];
  slopePerHour: number;
};

export type RecoveryTrendRead = {
  forecasts: readonly MetalForecastRead[];
  guardrailLine: string;
  charts: readonly ControlChartViewModel[];
  compactCharts: boolean;
  advisoryForecastLine: string;
};

function metalForecast(
  seriesId: string,
  readingId: string,
  metal: string,
  state: SessionState,
): MetalForecastRead {
  const points = trailingNumericPoints(seriesId, state, TRAILING_POINTS);
  const current = points[points.length - 1]?.value ?? 0;
  const first = points[0]?.value ?? current;
  const changePts = current - first;
  const slopePerHour = leastSquaresSlopePerHour(points);
  const { end } = shiftOf(state.cursor);
  const hoursToEnd = diffMinutes(end, state.cursor) / 60;
  const projected = current + slopePerHour * hoursToEnd;
  const design = designForReading(readingId);
  const atRisk = slopePerHour < 0 && projected < design;

  const changeSign = changePts >= 0 ? '+' : '−';
  const changeClause = `${changeSign}${formatNumber(Math.abs(changePts))} pts over 7 d`;
  let sentence = `${metal} ${formatNumber(current)} % · ${changeClause} · design ${formatNumber(design)} %`;

  const projectedRounded = formatNumber(projected);
  const currentRounded = formatNumber(current);
  if (slopePerHour < 0 && projectedRounded !== currentRounded) {
    sentence += ` · at this rate ${projectedRounded} % by shift end`;
  }

  return {
    metal,
    current,
    projected,
    design,
    atRisk,
    sentence,
    points,
    slopePerHour,
  };
}

function chartForMetal(
  readingId: string,
  metal: string,
  points: readonly { at: SeriesTime; value: number }[],
): ControlChartViewModel {
  return {
    variableId: readingId,
    readingId,
    readingLabel: `${metal} recovery`,
    phase: 'locked',
    points: points.map((p) => ({ ...p, highlighted: false })),
    events: [],
    trend: 'stable',
  };
}

function guardrailLine(
  state: SessionState,
  forecasts: readonly MetalForecastRead[],
): string {
  const findings = reagentStandingFindings(state);
  if (findings.size === 0) return '';

  let bestRowId: string | undefined;
  let bestFinding: { deltaPct: number; sinceDays: number } | undefined;
  for (const [rowId, finding] of findings) {
    if (!bestFinding || Math.abs(finding.deltaPct) > Math.abs(bestFinding.deltaPct)) {
      bestRowId = rowId;
      bestFinding = finding;
    }
  }
  if (!bestRowId || !bestFinding) return '';

  const readingId = REAGENT_READING_BY_ROW[bestRowId];
  const reading = readingId ? readingByIdLookup(readingId) : undefined;
  const label = reading?.label ?? 'Reagent';
  const direction = bestFinding.deltaPct < 0 ? 'under' : 'over';
  const delta = Math.abs(bestFinding.deltaPct);

  const recoveryMetal = readingId ? recoveryMetalForReagent(readingId) : 'Zn';
  const recoveryForecast = forecasts.find((f) => f.metal === recoveryMetal);
  const metalCurrent = recoveryForecast?.current ?? 0;
  const metalDesign = recoveryForecast?.design ?? 0;
  const metalSlope = recoveryForecast?.slopePerHour ?? 0;
  const seriesId =
    RECOVERY_SERIES.find((row) => row.metal === recoveryMetal)?.seriesId ?? 'rs-zn-rec';

  let tail: string;
  if (metalSlope >= 0 && metalCurrent >= metalDesign) {
    tail = 'holding';
  } else if (metalCurrent < metalDesign) {
    const ptsBelow = formatNumber(metalDesign - metalCurrent);
    const daysBelow = consecutivePointsBelowDesign(seriesId, metalDesign, state);
    const dayLabel = daysBelow === 1 ? 'day' : 'days';
    tail = `${recoveryMetal} recovery ${ptsBelow} pts below design for ${daysBelow} ${dayLabel}`;
  } else {
    const breach = shiftsToBreach(metalCurrent, metalDesign, metalSlope);
    tail =
      breach === 'holding'
        ? 'holding'
        : `${recoveryMetal} recovery below target in ${breach} shifts at this rate`;
  }

  return `${label} ${delta} % ${direction} design · ${bestFinding.sinceDays} days · ${tail}`;
}

export function recoveryForecastRead(
  state: SessionState,
  options?: { compactCharts?: boolean },
): RecoveryTrendRead {
  const forecasts = RECOVERY_SERIES.map((row) =>
    metalForecast(row.seriesId, row.readingId, row.metal, state),
  );
  const guardrail = guardrailLine(state, forecasts);
  const compactCharts = options?.compactCharts ?? false;
  const charts = RECOVERY_SERIES.map((row) => {
    const forecast = forecasts.find((f) => f.metal === row.metal)!;
    return chartForMetal(row.readingId, row.metal, forecast.points);
  });
  const advisoryForecastLine = guardrail || forecasts.map((f) => f.sentence).join(' · ');
  return {
    forecasts,
    guardrailLine: guardrail,
    charts,
    compactCharts,
    advisoryForecastLine,
  };
}
